import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle, XCircle, ExternalLink, Edit, ChevronDown, Eye, EyeOff } from 'lucide-react'
import {
  useChekiForm,
  useChekiOrders,
  useVerifyChekiOrder,
  useBatchVerifyChekiOrders,
  useToggleChekiPickup,
  useBatchToggleChekiPickup,
  useTenantChekiGroupSetting,
  type ChekiOrder,
  type ChekiForm,
} from '@/hooks/use-cheki'
import { formatDate, formatCurrency } from '@/lib/utils'
import ChekiFormModal from '@/components/cheki/cheki-form-modal'
import RejectNoteModal from '@/components/cheki/reject-note-modal'
import KasirOTSPanel from '@/components/cheki/kasir-ots-panel'
import KalkulasiPanel from '@/components/cheki/kalkulasi-panel'
import { FeatureGate } from '@/components/feature-gate'

// ── Analytics helpers ─────────────────────────────────────────────────────────

function ProgressBar({ pct, color = 'bg-primary' }: { pct: number; color?: string }) {
  return (
    <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(2, pct)}%` }} />
    </div>
  )
}

function ChekiAnalyticsPanel({ orders, form }: { orders: ChekiOrder[]; form: ChekiForm }) {
  const totalOrders = orders.length
  const totalSubmissions = new Set(
    orders.map((o) => o.session_id ?? (o.custom_data?.transaction_group_id as string | undefined) ?? o.id)
  ).size
  const totalQty = orders.reduce((s, o) => s + (o.quantity ?? 1), 0)
  const verifiedOrders = orders.filter((o) => o.status === 'verified')
  const verifiedQty = verifiedOrders.reduce((s, o) => s + (o.quantity ?? 1), 0)
  const verifiedRevenue = verifiedQty * form.price_idr
  const resolved = orders.filter((o) => o.status === 'verified' || o.status === 'rejected').length
  const verificationRate = resolved > 0 ? Math.round((verifiedOrders.length / resolved) * 100) : null

  // Channel breakdown
  const poOrders = orders.filter((o) => !o.is_ots)
  const otsOrders = orders.filter((o) => o.is_ots)
  const poQty = poOrders.reduce((s, o) => s + (o.quantity ?? 1), 0)
  const otsQty = otsOrders.reduce((s, o) => s + (o.quantity ?? 1), 0)
  const poSubmissions = new Set(poOrders.map((o) => o.session_id ?? o.id)).size
  const otsSubmissions = new Set(
    otsOrders.map((o) => (o.custom_data?.transaction_group_id as string | undefined) ?? o.id)
  ).size

  // Group cheki breakdown
  const groupOrders = orders.filter((o) => o.is_group_cheki)
  const groupQty = groupOrders.reduce((s, o) => s + (o.quantity ?? 1), 0)
  const poGroupQty = poOrders.filter((o) => o.is_group_cheki).reduce((s, o) => s + (o.quantity ?? 1), 0)
  const otsGroupQty = otsOrders.filter((o) => o.is_group_cheki).reduce((s, o) => s + (o.quantity ?? 1), 0)

  const statusBars: Array<{ key: ChekiOrder['status']; label: string; color: string }> = [
    { key: 'verified', label: 'Terverifikasi', color: 'bg-green-500' },
    { key: 'proof_uploaded', label: 'Bukti Dikirim', color: 'bg-blue-500' },
    { key: 'pending', label: 'Menunggu', color: 'bg-yellow-500' },
    { key: 'rejected', label: 'Ditolak', color: 'bg-red-500' },
    { key: 'cancelled', label: 'Dibatalkan', color: 'bg-gray-400' },
  ]

  const pickupEntries = [
    { key: 'diambil_ditempat', label: 'Ambil di Tempat' },
    { key: 'titip_member', label: 'Titip Member' },
    { key: 'titip_teman', label: 'Titip Teman' },
  ]
    .map(({ key, label }) => ({ label, count: orders.filter((o) => o.pickup_method === key).length }))
    .filter((e) => e.count > 0)

  const memberMap = new Map<string, { name: string; orderCount: number; qty: number; poQty: number; otsQty: number }>()
  for (const o of orders) {
    if (!o.member_id || !o.idol_members) continue
    const name = o.idol_members.stage_name ?? o.idol_members.name
    const prev = memberMap.get(o.member_id) ?? { name, orderCount: 0, qty: 0, poQty: 0, otsQty: 0 }
    const q = o.quantity ?? 1
    memberMap.set(o.member_id, {
      name,
      orderCount: prev.orderCount + 1,
      qty: prev.qty + q,
      poQty: prev.poQty + (o.is_ots ? 0 : q),
      otsQty: prev.otsQty + (o.is_ots ? q : 0),
    })
  }
  const memberDist = [...memberMap.values()].sort((a, b) => b.qty - a.qty)

  const dateCountMap = new Map<string, number>()
  for (const o of orders) {
    const dateStr = o.created_at.slice(0, 10)
    dateCountMap.set(dateStr, (dateCountMap.get(dateStr) ?? 0) + 1)
  }
  const trend = [...dateCountMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateStr, count]) => ({
      label: new Date(dateStr + 'T00:00:00').toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: '2-digit' }),
      count,
    }))
  const maxTrend = Math.max(...trend.map((d) => d.count), 1)

  if (totalOrders === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Belum ada pembelian untuk dianalisis.</p>
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Pembelian', value: totalSubmissions },
          { label: 'Total Cheki', value: totalQty },
          { label: 'Pendapatan Terverifikasi', value: formatCurrency(verifiedRevenue) },
          { label: 'Tingkat Verifikasi', value: verificationRate != null ? `${verificationRate}%` : '—' },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Channel breakdown */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <h3 className="text-sm font-semibold">Saluran Pembelian</h3>
          <div className="space-y-3">
            {([
              { label: 'PO', submissions: poSubmissions, items: poOrders.length, qty: poQty, color: 'bg-primary' },
              { label: 'OTS', submissions: otsSubmissions, items: otsOrders.length, qty: otsQty, color: 'bg-sky-500' },
            ] as const).map(({ label, submissions, items, qty, color }) => {
              const pct = totalQty > 0 ? Math.round((qty / totalQty) * 100) : 0
              return (
                <div key={label} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium">{label}</span>
                    <span className="text-muted-foreground">
                      {submissions} pembelian · {items} item · {qty} cheki · {pct}%
                    </span>
                  </div>
                  <ProgressBar pct={pct} color={color} />
                </div>
              )
            })}
          </div>
        </div>

        {/* Quota utilization */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <h3 className="text-sm font-semibold">Penggunaan Kuota</h3>
          <div className="space-y-3">
            {(() => {
              const poMemberQty = poQty - poGroupQty
              const otsMemberQty = otsQty - otsGroupQty
              const rows = [
                {
                  label: 'PO',
                  sold: poMemberQty,
                  quota: form.total_quota ?? null,
                  color: 'bg-primary',
                },
                {
                  label: 'OTS',
                  sold: otsMemberQty,
                  quota: form.total_quota_ots > 0 ? form.total_quota_ots : null,
                  color: 'bg-sky-500',
                },
                {
                  label: 'Group PO',
                  sold: poGroupQty,
                  quota: form.group_cheki_quota ?? null,
                  color: 'bg-purple-500',
                },
                {
                  label: 'Group OTS',
                  sold: otsGroupQty,
                  quota: form.group_cheki_quota_ots > 0 ? form.group_cheki_quota_ots : null,
                  color: 'bg-violet-400',
                },
              ].filter((r) => r.sold > 0 || r.quota != null)
              if (rows.length === 0) return <p className="text-xs text-muted-foreground">Belum ada data.</p>
              return rows.map(({ label, sold, quota, color }) => {
                const pct = quota != null ? Math.min(100, Math.round((sold / quota) * 100)) : null
                return (
                  <div key={label} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium">{label}</span>
                      <span className="text-muted-foreground">
                        {sold}{quota != null ? ` / ${quota}` : ''} cheki
                        {pct != null && <span className="ml-1">({pct}%)</span>}
                      </span>
                    </div>
                    {pct != null
                      ? <ProgressBar pct={pct} color={color} />
                      : <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                          <div className={`h-full rounded-full ${color}`} style={{ width: '100%', opacity: 0.3 }} />
                        </div>
                    }
                  </div>
                )
              })
            })()}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Status distribution */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <h3 className="text-sm font-semibold">Distribusi Status</h3>
          <div className="space-y-2.5">
            {statusBars.map(({ key, label, color }) => {
              const count = orders.filter((o) => o.status === key).length
              if (count === 0) return null
              const pct = Math.round((count / totalOrders) * 100)
              return (
                <div key={key} className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{label}</span>
                    <span>{count} ({pct}%)</span>
                  </div>
                  <ProgressBar pct={pct} color={color} />
                </div>
              )
            })}
          </div>
        </div>

        {/* Pickup method */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <h3 className="text-sm font-semibold">Metode Pengambilan</h3>
          {pickupEntries.length === 0 ? (
            <p className="text-xs text-muted-foreground">Tidak ada data.</p>
          ) : (
            <div className="space-y-2.5">
              {(() => {
                const totalPickup = pickupEntries.reduce((s, e) => s + e.count, 0)
                return pickupEntries.map(({ label, count }) => {
                  const pct = totalPickup > 0 ? Math.round((count / totalPickup) * 100) : 0
                  return (
                    <div key={label} className="space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{label}</span>
                        <span>{count} ({pct}%)</span>
                      </div>
                      <ProgressBar pct={pct} />
                    </div>
                  )
                })
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Member distribution */}
      {memberDist.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <h3 className="text-sm font-semibold">Distribusi Per Member</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs text-muted-foreground">
                <th className="pb-2 text-left font-medium">Member</th>
                <th className="pb-2 text-right font-medium">PO</th>
                <th className="pb-2 text-right font-medium">OTS</th>
                <th className="pb-2 text-right font-medium">Total</th>
                <th className="pb-2 text-right font-medium">%</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(() => {
                const memberTotalQty = memberDist.reduce((s, m) => s + m.qty, 0)
                return memberDist.map((m) => (
                  <tr key={m.name}>
                    <td className="py-2.5 font-medium">{m.name}</td>
                    <td className="py-2.5 text-right text-muted-foreground">{m.poQty > 0 ? m.poQty : '—'}</td>
                    <td className="py-2.5 text-right text-muted-foreground">{m.otsQty > 0 ? m.otsQty : '—'}</td>
                    <td className="py-2.5 text-right font-semibold">{m.qty}</td>
                    <td className="py-2.5 text-right text-muted-foreground">
                      {memberTotalQty > 0 ? `${Math.round((m.qty / memberTotalQty) * 100)}%` : '—'}
                    </td>
                  </tr>
                ))
              })()}
            </tbody>
            <tfoot>
              <tr className="border-t border-border font-semibold">
                <td className="pt-2.5">Total</td>
                <td className="pt-2.5 text-right">
                  {memberDist.reduce((s, m) => s + m.poQty, 0) > 0 ? memberDist.reduce((s, m) => s + m.poQty, 0) : '—'}
                </td>
                <td className="pt-2.5 text-right">
                  {memberDist.reduce((s, m) => s + m.otsQty, 0) > 0 ? memberDist.reduce((s, m) => s + m.otsQty, 0) : '—'}
                </td>
                <td className="pt-2.5 text-right">{memberDist.reduce((s, m) => s + m.qty, 0)}</td>
                <td className="pt-2.5 text-right text-muted-foreground font-normal">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Group cheki distribution */}
      {groupQty > 0 && (
        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <h3 className="text-sm font-semibold">Cheki Group</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs text-muted-foreground">
                <th className="pb-2 text-left font-medium">Sumber</th>
                <th className="pb-2 text-right font-medium">Cheki</th>
                {form.group_cheki_quota != null && (
                  <th className="pb-2 text-right font-medium">% Kuota</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {poGroupQty > 0 && (
                <tr>
                  <td className="py-2.5 text-muted-foreground">PO</td>
                  <td className="py-2.5 text-right font-semibold">{poGroupQty}</td>
                  {form.group_cheki_quota != null && (
                    <td className="py-2.5 text-right text-muted-foreground">
                      {Math.round((poGroupQty / form.group_cheki_quota) * 100)}%
                    </td>
                  )}
                </tr>
              )}
              {otsGroupQty > 0 && (
                <tr>
                  <td className="py-2.5 text-muted-foreground">OTS</td>
                  <td className="py-2.5 text-right font-semibold">{otsGroupQty}</td>
                  {form.group_cheki_quota != null && (
                    <td className="py-2.5 text-right text-muted-foreground">
                      {Math.round((otsGroupQty / form.group_cheki_quota) * 100)}%
                    </td>
                  )}
                </tr>
              )}
              <tr className="font-medium">
                <td className="py-2.5">Total</td>
                <td className="py-2.5 text-right">{groupQty}</td>
                {form.group_cheki_quota != null && (
                  <td className="py-2.5 text-right text-muted-foreground">
                    {Math.min(100, Math.round((groupQty / form.group_cheki_quota) * 100))}% dari kuota {form.group_cheki_quota}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Daily trend */}
      <div className="rounded-xl border border-border bg-card p-5 space-y-3">
        <h3 className="text-sm font-semibold">Aktivitas Pembelian per Hari</h3>
        <div className="space-y-1.5">
          {trend.map(({ label, count }) => (
            <div key={label} className="flex items-center gap-3 text-xs">
              <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
              <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full"
                  style={{ width: `${Math.round((count / maxTrend) * 100)}%` }}
                />
              </div>
              <span className="w-4 text-right tabular-nums text-muted-foreground">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Order grouping ────────────────────────────────────────────────────────────

type OrderGroup = {
  groupKey: string
  session_id: string | null
  fan_name: string
  fan_email: string
  fan_phone: string
  pickup_method: ChekiOrder['pickup_method']
  created_at: string
  orders: ChekiOrder[]
  totalQty: number
}

function groupOrders(orders: ChekiOrder[]): OrderGroup[] {
  const map = new Map<string, OrderGroup>()
  for (const o of orders) {
    const key = o.is_ots
      ? ((o.custom_data?.transaction_group_id as string | undefined) ?? `solo_${o.id}`)
      : (o.session_id ?? `solo_${o.id}`)
    if (!map.has(key)) {
      map.set(key, {
        groupKey: key,
        session_id: o.session_id ?? null,
        fan_name: o.fan_name,
        fan_email: o.fan_email,
        fan_phone: o.fan_phone,
        pickup_method: o.pickup_method,
        created_at: o.created_at,
        orders: [],
        totalQty: 0,
      })
    }
    const g = map.get(key)!
    g.orders.push(o)
    g.totalQty += o.quantity ?? 1
    if (o.created_at < g.created_at) g.created_at = o.created_at
  }
  return [...map.values()].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )
}

// ── Pickup panel (grouped) ────────────────────────────────────────────────────

function ChekiPickupPanel({ orders, formId }: { orders: ChekiOrder[]; formId: string }) {
  const toggle = useToggleChekiPickup()
  const batchToggle = useBatchToggleChekiPickup()
  const [expandedPickupGroups, setExpandedPickupGroups] = useState<Set<string>>(new Set())

  function togglePickupGroup(key: string) {
    setExpandedPickupGroups((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const verified = orders.filter((o) => o.status === 'verified')
  const groups = groupOrders(verified).sort((a, b) => {
    const aAllTaken = a.orders.every((o) => o.pickup_status === 'taken')
    const bAllTaken = b.orders.every((o) => o.pickup_status === 'taken')
    if (aAllTaken !== bAllTaken) return aAllTaken ? 1 : -1
    return a.fan_name.localeCompare(b.fan_name)
  })
  const takenGroupCount = groups.filter((g) => g.orders.every((o) => o.pickup_status === 'taken')).length

  if (verified.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Belum ada pesanan terverifikasi.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex items-center justify-between rounded-lg bg-muted/50 px-4 py-2.5 text-sm">
        <span className="text-muted-foreground">Progress Pengambilan</span>
        <span className="font-semibold tabular-nums">
          {takenGroupCount} / {groups.length} pembelian sudah diambil
        </span>
      </div>

      {/* Pickup table */}
      <div className="rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Fan</th>
              <th className="px-4 py-3 text-left font-medium">Member</th>
              <th className="px-4 py-3 text-center font-medium">Qty</th>
              <th className="px-4 py-3 text-center font-medium whitespace-nowrap">Diambil</th>
              <th className="px-4 py-3 text-right font-medium">Status</th>
              <th className="px-4 py-3 text-center font-medium">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {groups.map((group) => {
              const isMulti = group.orders.length > 1
              const isExpanded = expandedPickupGroups.has(group.groupKey)
              const takenInGroup = group.orders.filter((o) => o.pickup_status === 'taken').length
              const singleOrder = group.orders[0]

              return (
                <React.Fragment key={group.groupKey}>
                  {/* Group header row */}
                  <tr
                    className={`bg-card hover:bg-muted/30 transition-colors ${
                      !isMulti && singleOrder.pickup_status === 'taken' ? 'opacity-60' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-medium">
                      <span>{group.fan_name}</span>
                      {group.orders[0].is_ots && (
                        <span className="ml-1.5 inline-flex rounded-full bg-sky-100 px-1.5 py-0.5 text-xs font-medium text-sky-700">OTS</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {isMulti
                        ? `${group.orders.length} member`
                        : singleOrder.is_group_cheki
                          ? <span className="text-primary font-medium text-xs">Foto Grup</span>
                          : singleOrder.idol_members
                            ? (singleOrder.idol_members.stage_name ?? singleOrder.idol_members.name)
                            : '—'}
                    </td>
                    <td className="px-4 py-3 text-center font-semibold">{group.totalQty}</td>
                    <td className="px-4 py-3 text-center text-xs text-muted-foreground">
                      {isMulti ? (
                        <span className={takenInGroup === group.orders.length ? 'text-green-600 font-medium' : ''}>
                          {takenInGroup} / {group.orders.length}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isMulti ? (
                        <button
                          onClick={() =>
                            batchToggle.mutate({
                              ids: group.orders.map((o) => o.id),
                              formId,
                              pickup_status:
                                takenInGroup === group.orders.length ? 'not_taken' : 'taken',
                            })
                          }
                          disabled={batchToggle.isPending}
                          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                            takenInGroup === group.orders.length
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          }`}
                        >
                          {takenInGroup === group.orders.length
                            ? 'Semua Sudah'
                            : 'Tandai Semua'}
                        </button>
                      ) : (
                        <button
                          onClick={() =>
                            toggle.mutate({
                              id: singleOrder.id,
                              formId,
                              pickup_status: singleOrder.pickup_status === 'taken' ? 'not_taken' : 'taken',
                            })
                          }
                          disabled={toggle.isPending}
                          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                            singleOrder.pickup_status === 'taken'
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          }`}
                        >
                          {singleOrder.pickup_status === 'taken' ? 'Sudah Diambil' : 'Belum Diambil'}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isMulti ? (
                        <button
                          onClick={() => togglePickupGroup(group.groupKey)}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
                        >
                          {isExpanded ? 'Tutup' : 'Detail'}
                          <ChevronDown
                            className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>

                  {/* Sub-rows for expanded multi-order groups */}
                  {isMulti && isExpanded && group.orders.map((order) => {
                    const isTaken = order.pickup_status === 'taken'
                    const memberName = order.is_group_cheki
                      ? 'Foto Grup'
                      : order.idol_members
                        ? (order.idol_members.stage_name ?? order.idol_members.name)
                        : '—'
                    return (
                      <tr
                        key={order.id}
                        className={`bg-muted/10 hover:bg-muted/20 transition-colors border-l-2 border-l-primary/20 ${isTaken ? 'opacity-60' : ''}`}
                      >
                        <td className="px-4 py-2.5 pl-8">
                          <span className="font-mono text-xs text-muted-foreground">
                            {order.booking_code}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">{memberName}</td>
                        <td className="px-4 py-2.5 text-center text-xs">{order.quantity ?? 1}</td>
                        <td className="px-4 py-2.5 text-center text-xs text-muted-foreground">—</td>
                        <td className="px-4 py-2.5 text-center text-xs text-muted-foreground">—</td>
                        <td className="px-4 py-2.5 text-center text-xs text-muted-foreground">—</td>
                      </tr>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<ChekiOrder['status'], { label: string; className: string }> = {
  pending: { label: 'Menunggu', className: 'bg-yellow-100 text-yellow-700' },
  proof_uploaded: { label: 'Bukti Dikirim', className: 'bg-blue-100 text-blue-700' },
  verified: { label: 'Terverifikasi', className: 'bg-green-100 text-green-700' },
  rejected: { label: 'Ditolak', className: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Dibatalkan', className: 'bg-gray-100 text-gray-700' },
}

const FORM_STATUS_CONFIG = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-600' },
  open: { label: 'Buka', className: 'bg-green-100 text-green-700' },
  closed: { label: 'Ditutup', className: 'bg-red-100 text-red-700' },
}

const PICKUP_LABELS: Record<string, string> = {
  diambil_ditempat: 'Ambil di Tempat',
  titip_member: 'Titip Member',
  titip_teman: 'Titip Teman',
}

type ActiveSection = 'po' | 'kasir_ots' | 'pickup' | 'analytics' | 'kalkulasi'
type POFilter = 'all' | 'pending' | 'proof_uploaded' | 'verified' | 'rejected'

const SECTIONS: Array<{ key: ActiveSection; label: string }> = [
  { key: 'po', label: 'Pre-Order (PO)' },
  { key: 'kasir_ots', label: 'Kasir OTS' },
  { key: 'pickup', label: 'Pengambilan' },
  { key: 'analytics', label: 'Analitik' },
  { key: 'kalkulasi', label: 'Kalkulasi' },
]

const PO_TABS: Array<{ key: POFilter; label: string }> = [
  { key: 'all', label: 'Semua' },
  { key: 'pending', label: 'Menunggu' },
  { key: 'proof_uploaded', label: 'Bukti Dikirim' },
  { key: 'verified', label: 'Terverifikasi' },
  { key: 'rejected', label: 'Ditolak' },
]

export default function ChekiFormDetailPage() {
  const { formId } = useParams<{ formId: string }>()
  const { data: form, isLoading: loadingForm } = useChekiForm(formId)
  const { data: orders = [], isLoading: loadingOrders } = useChekiOrders(formId)
  const { query: groupSettingQuery } = useTenantChekiGroupSetting()
  const hasGroupCheki = groupSettingQuery.data?.has_cheki_group ?? false
  const verify = useVerifyChekiOrder()
  const batchVerify = useBatchVerifyChekiOrders()

  const [activeSection, setActiveSection] = useState<ActiveSection>('po')
  const [poFilter, setPOFilter] = useState<POFilter>('all')
  const [editModalOpen, setEditModalOpen] = useState(false)
  // Array of order IDs to reject (single or batch from a session group)
  const [rejectTarget, setRejectTarget] = useState<string[] | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [showPoRevenue, setShowPoRevenue] = useState(false)
  const [showPOSection, setShowPOSection] = useState(false)
  const [showPengambilanSection, setShowPengambilanSection] = useState(false)

  function toggleRow(id: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function toggleGroup(key: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const poOrders = orders.filter((o) => !o.is_ots)
  const otsOrders = orders.filter((o) => o.is_ots)
  const filteredOrders =
    poFilter === 'all' ? poOrders : poOrders.filter((o) => o.status === poFilter)
  const filteredGroups = groupOrders(filteredOrders)
  const verifiedOrders = orders.filter((o) => o.status === 'verified')
  const verifiedSessionMap = new Map<string, typeof verifiedOrders>()
  for (const o of verifiedOrders) {
    const key = o.session_id ?? (o.custom_data?.transaction_group_id as string | undefined) ?? o.id
    verifiedSessionMap.set(key, [...(verifiedSessionMap.get(key) ?? []), o])
  }
  const verifiedSessionCount = verifiedSessionMap.size
  const takenSessionCount = [...verifiedSessionMap.values()].filter(
    (grp) => grp.every((o) => o.pickup_status === 'taken')
  ).length
  const poQty = poOrders.reduce((s, o) => s + (o.quantity ?? 1), 0)
  const poSubmissionCount = new Set(poOrders.map((o) => o.session_id ?? o.id)).size
  const poVerifiedRevenue = poOrders
    .filter((o) => o.status === 'verified')
    .reduce((s, o) => {
      const price = o.is_group_cheki ? (form?.group_price_idr ?? form?.price_idr ?? 0) : (form?.price_idr ?? 0)
      return s + (o.quantity ?? 1) * price
    }, 0)
  const otsQty = otsOrders.reduce((s, o) => s + (o.quantity ?? 1), 0)
  const otsSubmissionCount = new Set(
    otsOrders.map((o) => (o.custom_data?.transaction_group_id as string) ?? o.id)
  ).size
  const otsGroupQty = otsOrders.filter((o) => o.is_group_cheki).reduce((s, o) => s + (o.quantity ?? 1), 0)
  const otsMemberQty = otsQty - otsGroupQty
  if (loadingForm) return <p className="text-muted-foreground p-6">Memuat form...</p>
  if (!form) return <p className="text-destructive p-6">Form tidak ditemukan.</p>

  const isGroup = form.quota_type === 'group'
  const formStatusCfg = FORM_STATUS_CONFIG[form.status]
  const hasCustomFields = (form.custom_fields ?? []).length > 0
  const colSpan = hasCustomFields ? 11 : 10

  return (
    <FeatureGate feature="cheki_form">
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/cheki"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Kembali ke Daftar Form
      </Link>

      {/* Header card */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">

        {/* Title + actions */}
        <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-4">
          <h1 className="text-xl font-bold">{form.title}</h1>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${formStatusCfg.className}`}
            >
              {formStatusCfg.label}
            </span>
            <button
              onClick={() => setEditModalOpen(true)}
              className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
            >
              <Edit className="h-3.5 w-3.5" />
              Edit Form
            </button>
          </div>
        </div>

        {/* ── Event ────────────────────────────────── */}
        <div className="px-5 py-3 border-t border-border">
          <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Event</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {form.events && (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Nama Event</p>
                <p className="font-medium">{form.events.title}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Form Dibuka</p>
              <p>{formatDate(form.opens_at, { dateStyle: 'medium', timeStyle: 'short' })}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Form Ditutup</p>
              <p>{formatDate(form.closes_at, { dateStyle: 'medium', timeStyle: 'short' })}</p>
            </div>
          </div>
        </div>

        {/* ── PO + OTS side-by-side ─────────────────── */}
        <div className="border-t border-border grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border">

        {/* ── Pre-Order (PO) ── */}
        <div className="px-5 py-3">
          <button
            onClick={() => setShowPOSection((v) => !v)}
            className="flex w-full items-center justify-between mb-2.5 hover:opacity-70 transition-opacity"
          >
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Pre-Order (PO)</span>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{poSubmissionCount} pembelian · {poQty} cheki</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showPOSection ? 'rotate-180' : ''}`} />
            </div>
          </button>
          {showPOSection && (<>
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm mb-3">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Harga</p>
              <p className="font-semibold text-primary">{formatCurrency(form.price_idr)}</p>
            </div>
            {hasGroupCheki && (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Harga Grup</p>
                {form.group_price_idr != null
                  ? <p className="font-semibold">{formatCurrency(form.group_price_idr)}</p>
                  : <p className="text-muted-foreground">—</p>
                }
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Pembelian</p>
              <p className="font-semibold">
                {poSubmissionCount}
                {poOrders.length > 0 && (
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    ({poOrders.length} item · {poQty} cheki)
                  </span>
                )}
              </p>
            </div>
          </div>
          {/* PO quota */}
          {isGroup ? (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {form.booked_count} terjual
                  {form.total_quota != null ? ` · kuota ${form.total_quota}` : ''}
                </span>
                {form.total_quota != null && (
                  <span>{Math.min(100, Math.round((form.booked_count / form.total_quota) * 100))}%</span>
                )}
              </div>
              {form.total_quota != null && (
                <ProgressBar pct={Math.min(100, Math.round((form.booked_count / form.total_quota) * 100))} />
              )}
              {hasGroupCheki && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Grup cheki: {form.group_cheki_booked_count} terjual{form.group_cheki_quota != null ? ` · kuota ${form.group_cheki_quota}` : ''}</span>
                    {form.group_cheki_quota != null && (
                      <span>{Math.min(100, Math.round((form.group_cheki_booked_count / form.group_cheki_quota) * 100))}%</span>
                    )}
                  </div>
                  {form.group_cheki_quota != null && (
                    <ProgressBar pct={Math.min(100, Math.round((form.group_cheki_booked_count / form.group_cheki_quota) * 100))} color="bg-purple-500" />
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              {(form.cheki_form_members ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Tidak ada member terdaftar.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(form.cheki_form_members ?? []).map((m) => {
                    const isFull = m.booked_count >= m.quota
                    return (
                      <div
                        key={m.id}
                        className={`rounded-lg border px-3 py-1.5 text-xs ${
                          isFull ? 'border-red-200 bg-red-50 text-red-700' : 'border-border bg-background'
                        }`}
                      >
                        <p className="font-medium">
                          {m.idol_members?.stage_name ?? m.idol_members?.name ?? '—'}
                        </p>
                        <p className="text-muted-foreground">
                          {m.booked_count} / {m.quota} slot{isFull && ' · Penuh'}
                        </p>
                      </div>
                    )
                  })}
                </div>
              )}
              {hasGroupCheki && (
                <div className="mt-1.5 space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Grup cheki: {form.group_cheki_booked_count} terjual{form.group_cheki_quota != null ? ` · kuota ${form.group_cheki_quota}` : ''}</span>
                    {form.group_cheki_quota != null && (
                      <span>{Math.min(100, Math.round((form.group_cheki_booked_count / form.group_cheki_quota) * 100))}%</span>
                    )}
                  </div>
                  {form.group_cheki_quota != null && (
                    <ProgressBar pct={Math.min(100, Math.round((form.group_cheki_booked_count / form.group_cheki_quota) * 100))} color="bg-purple-500" />
                  )}
                </div>
              )}
            </>
          )}
          </>)}
        </div>

        {/* ── On-The-Spot (OTS) ── */}
        <div className="px-5 py-3">
          <button
            onClick={() => setShowPOSection((v) => !v)}
            className="flex w-full items-center justify-between mb-2.5 hover:opacity-70 transition-opacity"
          >
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">On-The-Spot (OTS)</span>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{otsSubmissionCount} pembelian · {otsQty} cheki</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showPOSection ? 'rotate-180' : ''}`} />
            </div>
          </button>
          {showPOSection && (<>
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm mb-3">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Harga</p>
              {form.price_ots_idr != null
                ? <p className="font-semibold text-sky-600">{formatCurrency(form.price_ots_idr)}</p>
                : <p className="text-muted-foreground">—</p>
              }
            </div>
            {hasGroupCheki && (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Harga Grup</p>
                {form.group_price_ots_idr != null
                  ? <p className="font-semibold text-sky-600">{formatCurrency(form.group_price_ots_idr)}</p>
                  : <p className="text-muted-foreground">—</p>
                }
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Pembelian</p>
              <p className="font-semibold">
                {otsSubmissionCount}
                {otsOrders.length > 0 && (
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    ({otsOrders.length} item · {otsQty} cheki)
                  </span>
                )}
              </p>
            </div>
          </div>
          {/* OTS quota (group mode) + group cheki info */}
          {(isGroup || hasGroupCheki) && (
            <div className="space-y-1.5">
              {isGroup && (
                <>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>
                      {otsMemberQty} terjual
                      {form.total_quota_ots > 0 ? ` · kuota ${form.total_quota_ots}` : ''}
                    </span>
                    {form.total_quota_ots > 0 && (
                      <span>{Math.min(100, Math.round((otsMemberQty / form.total_quota_ots) * 100))}%</span>
                    )}
                  </div>
                  {form.total_quota_ots > 0 && (
                    <ProgressBar pct={Math.min(100, Math.round((otsMemberQty / form.total_quota_ots) * 100))} color="bg-sky-500" />
                  )}
                </>
              )}
              {hasGroupCheki && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Grup cheki: {otsGroupQty} terjual{form.group_cheki_quota_ots > 0 ? ` · kuota ${form.group_cheki_quota_ots}` : ''}</span>
                    {form.group_cheki_quota_ots > 0 && (
                      <span>{Math.min(100, Math.round((otsGroupQty / form.group_cheki_quota_ots) * 100))}%</span>
                    )}
                  </div>
                  {form.group_cheki_quota_ots > 0 && (
                    <ProgressBar pct={Math.min(100, Math.round((otsGroupQty / form.group_cheki_quota_ots) * 100))} color="bg-purple-500" />
                  )}
                </div>
              )}
            </div>
          )}
          </>)}
        </div>

        </div>{/* end PO+OTS grid */}

        {/* ── Pengambilan ──────────────────────────── */}
        <div className="px-5 py-3 border-t border-border">
          <button
            onClick={() => setShowPengambilanSection((v) => !v)}
            className="flex w-full items-center justify-between mb-2.5 hover:opacity-70 transition-opacity"
          >
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Pengambilan</span>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{takenSessionCount} / {verifiedSessionCount} diambil</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showPengambilanSection ? 'rotate-180' : ''}`} />
            </div>
          </button>
          {showPengambilanSection && (verifiedSessionCount > 0 ? (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{takenSessionCount} / {verifiedSessionCount} pembelian diambil</span>
                <span className="font-semibold">{Math.round((takenSessionCount / verifiedSessionCount) * 100)}%</span>
              </div>
              <ProgressBar
                pct={Math.round((takenSessionCount / verifiedSessionCount) * 100)}
                color="bg-green-500"
              />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Belum ada pesanan terverifikasi.</p>
          ))}
        </div>

      </div>

      {/* Main section tabs */}
      <div className="space-y-3">
        {/* Section tabs (top level) */}
        <div className="flex flex-wrap gap-1.5 border-b border-border pb-3">
          {SECTIONS.map((section) => {
            const count =
              section.key === 'po'
                ? poSubmissionCount || undefined
                : section.key === 'kasir_ots'
                  ? otsSubmissionCount || undefined
                  : undefined
            return (
              <button
                key={section.key}
                onClick={() => setActiveSection(section.key)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeSection === section.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                {section.label}
                {count != null && (
                  <span className="ml-1.5 opacity-70">({count})</span>
                )}
              </button>
            )
          })}
        </div>

        {/* Analytics panel */}
        {activeSection === 'analytics' && (
          <ChekiAnalyticsPanel orders={orders} form={form} />
        )}

        {/* Kalkulasi panel */}
        {activeSection === 'kalkulasi' && (
          <KalkulasiPanel orders={orders} form={form} />
        )}

        {/* Pickup panel */}
        {activeSection === 'pickup' && (
          <ChekiPickupPanel orders={orders} formId={formId!} />
        )}

        {/* Kasir OTS panel */}
        {activeSection === 'kasir_ots' && (
          <KasirOTSPanel
            form={form}
            formId={formId!}
            orders={orders}
            hasGroupCheki={hasGroupCheki}
          />
        )}

        {/* PO section */}
        {activeSection === 'po' && (
          <div className="space-y-3">
            {/* PO summary stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Pembelian', value: String(poSubmissionCount), isRevenue: false },
                { label: 'Total Cheki', value: String(poQty), isRevenue: false },
                { label: 'Pendapatan', value: formatCurrency(poVerifiedRevenue), isRevenue: true },
              ].map(({ label, value, isRevenue }) => (
                <div key={label} className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  {isRevenue ? (
                    <div className="mt-1 flex items-center gap-2">
                      <p className="text-xl font-bold tabular-nums">
                        {showPoRevenue ? value : '••••••'}
                      </p>
                      <button
                        onClick={() => setShowPoRevenue((v) => !v)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPoRevenue ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  ) : (
                    <p className="mt-1 text-xl font-bold tabular-nums">{value}</p>
                  )}
                </div>
              ))}
            </div>

            {/* PO sub-tabs (status filter) */}
            <div className="flex flex-wrap gap-2">
              {PO_TABS.map((tab) => {
                const filtered = tab.key === 'all' ? poOrders : poOrders.filter((o) => o.status === tab.key)
                const count = new Set(filtered.map((o) => o.session_id ?? o.id)).size
                return (
                  <button
                    key={tab.key}
                    onClick={() => setPOFilter(tab.key)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      poFilter === tab.key
                        ? 'bg-primary/15 text-primary border border-primary/30'
                        : 'bg-muted text-muted-foreground hover:bg-accent'
                    }`}
                  >
                    {tab.label}
                    {count > 0 && (
                      <span className="ml-1.5 opacity-70">({count})</span>
                    )}
                  </button>
                )
              })}
            </div>

          <div className="rounded-xl border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Kode</th>
                  <th className="px-4 py-3 text-left font-medium">Fan</th>
                  <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Telepon</th>
                  <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Pengambilan</th>
                  <th className="px-4 py-3 text-left font-medium">Member</th>
                  <th className="px-4 py-3 text-center font-medium">Qty</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Bukti</th>
                  <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Tanggal</th>
                  {hasCustomFields && (
                    <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Field Tambahan</th>
                  )}
                  <th className="px-4 py-3 text-right font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loadingOrders ? (
                  <tr>
                    <td colSpan={colSpan} className="px-4 py-8 text-center text-muted-foreground">
                      Memuat pesanan...
                    </td>
                  </tr>
                ) : filteredGroups.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="px-4 py-8 text-center text-muted-foreground">
                      Tidak ada pesanan.
                    </td>
                  </tr>
                ) : (
                  filteredGroups.map((group) => {
                    const isMulti = group.orders.length > 1
                    const isExpanded = expandedGroups.has(group.groupKey)

                    if (!isMulti) {
                      // ── Single-order group: flat row (no expand chevron) ──
                      const order = group.orders[0]
                      const statusCfg = STATUS_CONFIG[order.status] ?? {
                        label: order.status,
                        className: 'bg-gray-100 text-gray-700',
                      }
                      const hasCustomData =
                        hasCustomFields &&
                        order.custom_data &&
                        Object.keys(order.custom_data).length > 0
                      const isRowExpanded = expandedRows.has(order.id)
                      return (
                        <React.Fragment key={group.groupKey}>
                          <tr className="bg-card hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                              {order.invoice_code ?? order.booking_code}
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-medium">{order.fan_name}</p>
                              <p className="text-xs text-muted-foreground">{order.fan_email}</p>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                              {order.fan_phone}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {PICKUP_LABELS[order.pickup_method] ?? order.pickup_method}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {order.idol_members
                                ? (order.idol_members.stage_name ?? order.idol_members.name)
                                : '—'}
                            </td>
                            <td className="px-4 py-3 text-center font-semibold">
                              {order.quantity ?? 1}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusCfg.className}`}
                              >
                                {statusCfg.label}
                              </span>
                              {order.is_ots && (
                                <span className="ml-1.5 inline-flex rounded-full px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
                                  OTS
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {order.proof_url ? (
                                <a
                                  href={order.proof_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                >
                                  Lihat <ExternalLink className="h-3 w-3" />
                                </a>
                              ) : (
                                <span className="text-xs text-muted-foreground">Belum ada</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                              {formatDate(order.created_at, { dateStyle: 'short', timeStyle: 'short' })}
                            </td>
                            {hasCustomFields && (
                              <td className="px-4 py-3">
                                {hasCustomData ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleRow(order.id)}
                                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                  >
                                    Lihat
                                    <ChevronDown
                                      className={`h-3 w-3 transition-transform ${isRowExpanded ? 'rotate-180' : ''}`}
                                    />
                                  </button>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </td>
                            )}
                            <td className="px-4 py-3">
                              {order.status === 'proof_uploaded' && (
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() =>
                                      verify.mutate({
                                        id: order.id,
                                        formId: order.form_id,
                                        status: 'verified',
                                      })
                                    }
                                    disabled={verify.isPending}
                                    className="rounded p-1 text-green-600 hover:bg-green-50 disabled:opacity-50"
                                    title="Verifikasi"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setRejectTarget([order.id])}
                                    className="rounded p-1 text-red-600 hover:bg-red-50"
                                    title="Tolak"
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                          {hasCustomData && isRowExpanded && (
                            <tr className="bg-muted/20 border-b border-border">
                              <td colSpan={colSpan} className="px-6 py-3">
                                <dl className="flex flex-wrap gap-x-6 gap-y-2">
                                  {(form.custom_fields ?? []).map((field) => {
                                    const val = order.custom_data?.[field.key]
                                    return (
                                      <div key={field.key} className="space-y-0.5">
                                        <dt className="text-xs text-muted-foreground">{field.label}</dt>
                                        <dd className="text-sm font-medium">
                                          {val != null && val !== '' ? String(val) : <span className="italic text-muted-foreground">—</span>}
                                        </dd>
                                      </div>
                                    )
                                  })}
                                </dl>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    }

                    // ── Multi-order group: collapsible header + sub-rows ──
                    const statusSet = [...new Set(group.orders.map((o) => o.status))]
                    const batchableIds = group.orders
                      .filter((o) => o.status === 'proof_uploaded')
                      .map((o) => o.id)

                    return (
                      <React.Fragment key={group.groupKey}>
                        {/* Group header */}
                        <tr className="bg-card hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                            {group.orders[0].invoice_code ?? group.orders[0].booking_code}
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium">{group.fan_name}</p>
                            <p className="text-xs text-muted-foreground">{group.fan_email}</p>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                            {group.fan_phone}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {PICKUP_LABELS[group.pickup_method] ?? group.pickup_method}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {group.orders.length} member
                          </td>
                          <td className="px-4 py-3 text-center font-semibold">{group.totalQty}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {statusSet.map((s) => {
                                const cfg = STATUS_CONFIG[s as ChekiOrder['status']] ?? {
                                  label: s,
                                  className: 'bg-gray-100 text-gray-700',
                                }
                                return (
                                  <span
                                    key={s}
                                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cfg.className}`}
                                  >
                                    {cfg.label}
                                  </span>
                                )
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {(() => {
                              const proofUrl = group.orders.find((o) => o.proof_url)?.proof_url
                              return proofUrl ? (
                                <a
                                  href={proofUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                >
                                  Lihat <ExternalLink className="h-3 w-3" />
                                </a>
                              ) : (
                                <span className="text-xs text-muted-foreground">Belum ada</span>
                              )
                            })()}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                            {formatDate(group.created_at, { dateStyle: 'short', timeStyle: 'short' })}
                          </td>
                          {hasCustomFields && (
                            <td className="px-4 py-3 text-xs text-muted-foreground">—</td>
                          )}
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {batchableIds.length > 0 && (
                                <>
                                  <button
                                    onClick={() =>
                                      batchVerify.mutate({
                                        ids: batchableIds,
                                        formId: formId!,
                                        status: 'verified',
                                      })
                                    }
                                    disabled={batchVerify.isPending}
                                    className="rounded p-1 text-green-600 hover:bg-green-50 disabled:opacity-50"
                                    title="Verifikasi semua"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setRejectTarget(batchableIds)}
                                    className="rounded p-1 text-red-600 hover:bg-red-50"
                                    title="Tolak semua"
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => toggleGroup(group.groupKey)}
                                className="rounded p-1.5 text-muted-foreground hover:bg-accent"
                                title={isExpanded ? 'Tutup' : 'Lihat detail'}
                              >
                                <ChevronDown
                                  className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Sub-rows */}
                        {isExpanded && group.orders.map((order) => {
                          const hasCustomData =
                            hasCustomFields &&
                            order.custom_data &&
                            Object.keys(order.custom_data).length > 0
                          const isRowExpanded = expandedRows.has(order.id)
                          return (
                            <React.Fragment key={order.id}>
                              <tr className="bg-muted/10 hover:bg-muted/20 transition-colors border-l-2 border-l-primary/20">
                                <td className="px-4 py-2.5 pl-8 font-mono text-xs text-muted-foreground whitespace-nowrap">
                                  {order.booking_code}
                                </td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground">—</td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground">—</td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground">—</td>
                                <td className="px-4 py-2.5 text-xs">
                                  {order.is_group_cheki
                                    ? <span className="rounded-full bg-purple-100 px-2 py-0.5 font-medium text-purple-700">Cheki Group</span>
                                    : order.idol_members
                                      ? (order.idol_members.stage_name ?? order.idol_members.name)
                                      : '—'}
                                </td>
                                <td className="px-4 py-2.5 text-center text-xs">
                                  {order.quantity ?? 1}
                                </td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground">—</td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground">—</td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground">—</td>
                                {hasCustomFields && (
                                  <td className="px-4 py-2.5">
                                    {hasCustomData ? (
                                      <button
                                        type="button"
                                        onClick={() => toggleRow(order.id)}
                                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                      >
                                        Lihat
                                        <ChevronDown
                                          className={`h-3 w-3 transition-transform ${isRowExpanded ? 'rotate-180' : ''}`}
                                        />
                                      </button>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                  </td>
                                )}
                                <td className="px-4 py-2.5 text-xs text-muted-foreground">—</td>
                              </tr>
                              {hasCustomData && isRowExpanded && (
                                <tr className="bg-muted/20 border-b border-border">
                                  <td colSpan={colSpan} className="px-6 py-3">
                                    <dl className="flex flex-wrap gap-x-6 gap-y-2">
                                      {(form.custom_fields ?? []).map((field) => {
                                        const val = order.custom_data?.[field.key]
                                        return (
                                          <div key={field.key} className="space-y-0.5">
                                            <dt className="text-xs text-muted-foreground">{field.label}</dt>
                                            <dd className="text-sm font-medium">
                                              {val != null && val !== '' ? String(val) : <span className="italic text-muted-foreground">—</span>}
                                            </dd>
                                          </div>
                                        )
                                      })}
                                    </dl>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          )
                        })}
                      </React.Fragment>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
          </div>
        )}
      </div>

      {/* Edit form modal */}
      <ChekiFormModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        form={form}
      />

      {/* Reject note modal */}
      <RejectNoteModal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        isPending={batchVerify.isPending}
        onConfirm={(note) =>
          batchVerify.mutate(
            {
              ids: rejectTarget!,
              formId: formId!,
              status: 'rejected',
              admin_note: note || undefined,
            },
            { onSuccess: () => setRejectTarget(null) }
          )
        }
      />
    </div>
    </FeatureGate>
  )
}
